import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { InvoiceInterface as Invoice, InvoiceInterface, PaymentInterface } from '@contracts/interfaces/invoice.interface';
import { InvoicesService } from '../../services/invoices.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { InvoiceDeleteDialogService } from 'src/app/dialogs/invoice-delete/invoice-delete.service.dialog';
import { PDFService } from 'src/app/services/pdf.service';
import { forkJoin, of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { PaymentDetailDialogComponent } from 'src/app/dialogs/payment-detail/payment-detail.dialog';
import { DateRange } from '@contracts/interfaces/transaction.interfaces';

interface InvoicePaymentRow {
  invoiceNumber: number;
  invoiceType?: string;
  invoiceStatus?: string;
  paymentId?: string;
  transactionSha1?: string | null;
  customer?: Invoice['customer'];
  payment: PaymentInterface;
  total?: number;
  amountDue?: number;
  dateCreated?: Date | string;
}

enum DateMode {
  Weekly,
  Monthly,
  Quaterly,
  Yearly,
  AllTime,
  Custom
}

@Component({
  selector: 'app-invoices',
  templateUrl: './invoices.component.html',
  styleUrls: ['./invoices.component.scss', '../table.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ]
})
export class InvoicesComponent implements OnInit {

  isLoading = true;

  DateMode = DateMode;
  dateMode: DateMode = DateMode.AllTime;
  dateOffset = 0;
  dateRange: DateRange = null;
  customDateRange: any = null;

  dataSourceActive: MatTableDataSource<Invoice>;
  dataSourceCompleted: MatTableDataSource<Invoice>;
  dataSourcePayments: MatTableDataSource<InvoicePaymentRow>;
  allActive: Invoice[] = [];
  allCompleted: Invoice[] = [];
  allReconciled: Invoice[] = [];
  dataSourceReconciled: MatTableDataSource<Invoice>;
  reconciledCount = 0;
  allRefunds: Invoice[] = [];
  dataSourceRefunds: MatTableDataSource<Invoice>;
  refundsCount = 0;
  allPayments: InvoicePaymentRow[] = [];
  pageSize = 100;
  activePage = 1;
  completedPage = 1;
  paymentsPage = 1;
  columnsToDisplay = ['status', 'date', 'number', 'custom', 'customer', 'paymentMethod', 'amountTotal', 'print'];
  paymentColumnsToDisplay = ['date', 'invoiceNumber', 'customer', 'paymentMethod', 'note', 'amount'];

  invoiceType: string = null;
  paymentMethodFilter: string = null;

  activeTab: string = "active";
  activeCount = 0;
  completedCount = 0;
  paymentsCount = 0;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  private readonly filterStorageKey = 'invoices.filters';
  private readonly dateStorageKey = 'invoicesDateRange';

  constructor(
    private invoicesService: InvoicesService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private invoiceDeleteDialog: InvoiceDeleteDialogService,
    private pdfService: PDFService,
    private dialog: MatDialog) { }

  ngOnInit(): void {
    this.route.data.subscribe(data => {
      const tabData = data['tab'];
      const allowedTabs = ['active', 'paid', 'reconciled', 'refunds', 'payments'];
      this.activeTab = allowedTabs.includes(tabData) ? tabData : 'active';
      this.dateRange = this.getDateRange();
      this.loadFiltersFromStorage();
      this.loadInvoices();
    });
  }

  loadInvoices() {
    this.dateRange = this.getDateRange();
    // Keep the current table visible while refreshing so scroll position is preserved.
    this.isLoading = !this.hasDataForCurrentTab;
    const baseQuery = this.buildQuery();
    const tab = this.activeTab;
    forkJoin({
      completed: this.invoicesService.find(baseQuery, ['paid', 'refund'], true),
      active: tab === 'active' ? this.invoicesService.find(baseQuery, ['unpaid']) : of([] as Invoice[]),
      payments: tab === 'payments' ? this.invoicesService.getPayments(baseQuery, this.paymentMethodFilter) : of([]),
      info: this.invoicesService.getInfo(baseQuery)
    }).subscribe(({ completed, active, payments, info }: { completed: Invoice[]; active: Invoice[]; payments: InvoicePaymentRow[]; info: any }) => {
      if (tab !== this.activeTab) return;
      const sorted = this.sortInvoices(completed ?? []);
      this.allCompleted = sorted.filter(invoice => invoice.status === 'paid' && !invoice.isReconciled);
      this.allReconciled = sorted.filter(invoice => invoice.status === 'paid' && invoice.isReconciled);
      this.allRefunds = sorted.filter(invoice => invoice.status === 'refund');
      this.refundsCount = this.allRefunds.length;
      this.dataSourceRefunds = new MatTableDataSource<Invoice>(this.allRefunds.slice(0, this.pageSize));
      this.completedCount = this.allCompleted.length;
      this.reconciledCount = this.allReconciled.length;
      this.dataSourceCompleted = new MatTableDataSource<Invoice>(this.allCompleted.slice(0, this.pageSize));
      this.dataSourceReconciled = new MatTableDataSource<Invoice>(this.allReconciled.slice(0, this.pageSize));
      this.completedPage = 1;

      if (tab === 'active') {
        this.allActive = this.sortInvoices(active ?? []);
        this.activePage = 1;
        this.dataSourceActive = new MatTableDataSource<Invoice>(this.allActive.slice(0, this.pageSize));
      } else if (tab === 'payments') {
        this.allPayments = this.sortPayments(payments ?? []);
        this.paymentsPage = 1;
        this.dataSourcePayments = new MatTableDataSource<InvoicePaymentRow>(this.allPayments.slice(0, this.pageSize));
      }
      this.activeCount = info?.activeCount ?? this.allActive.length;
      this.paymentsCount = info?.paymentsCount ?? this.allPayments.length;
      this.isLoading = false;
      this.cdr.detectChanges();
    });
  }

  deleteInvoice(invoice: number) {
    this.invoiceDeleteDialog.open(invoice, r => {
      this.loadInvoices();
    });
  }

  filter(invoiceType: string | null) {
    this.invoiceType = invoiceType;
    this.persistFilters();
    this.loadInvoices();
  }

  filterPaymentMethod(paymentMethod: string | null) {
    this.paymentMethodFilter = paymentMethod ? paymentMethod.toLowerCase() : null;
    this.persistFilters();
    this.loadInvoices();
  }

  get completedTabDataSource(): MatTableDataSource<Invoice> {
    if (this.activeTab === 'refunds') return this.dataSourceRefunds;
    return this.activeTab === 'reconciled' ? this.dataSourceReconciled : this.dataSourceCompleted;
  }

  get completedTabCount(): number {
    if (this.activeTab === 'refunds') return this.refundsCount;
    return this.activeTab === 'reconciled' ? this.reconciledCount : this.completedCount;
  }

  get hasDataForCurrentTab(): boolean {
    if (this.activeTab === 'refunds') return !!this.dataSourceRefunds;
    if (this.activeTab === 'paid') {
      return !!this.dataSourceCompleted;
    }
    if (this.activeTab === 'reconciled') {
      return !!this.dataSourceReconciled;
    }
    if (this.activeTab === 'payments') {
      return !!this.dataSourcePayments;
    }
    return !!this.dataSourceActive;
  }

  private buildQuery(): any {
    const query: any = {};
    if (this.invoiceType) {
      query.type = this.invoiceType;
    }
    if (this.paymentMethodFilter) {
      query["paymentsMade.method"] = { $regex: `^${this.paymentMethodFilter}$`, $options: 'i' };
    }
    return query;
  }

  editInvoice(number: number) {
    //this.router.navigate(["view-invoice", number])
    this.invoicesService.get(number).subscribe((invoice: Invoice) => {
      this.invoicesService.openInvoiceDialog(invoice, (r: any) => {
        this.loadInvoices();
      })
    });
  }

  print (invoice: Invoice, event) {
    this.pdfService.download(invoice.pdf.file, invoice, invoice.number + "-invoice.pdf", (r: any)=>{
      return r;
    });
    event.stopPropagation();
  }

  openPayment(row: InvoicePaymentRow) {
    const ref = this.dialog.open(PaymentDetailDialogComponent, {
      width: '520px',
      data: row
    });
    ref.afterClosed().subscribe(result => {
      if (result?.linked) {
        this.loadInvoices();
      }
    });
  }

  createInvoice(type: string) {
      this.invoicesService.openCreateInvoiceDialog(type, () => {
        this.loadInvoices();
      });
  }

  getCustomer(invoice: { customer: InvoiceInterface['customer'] }) {
    const customer = invoice?.customer as any;
    if (!customer) {
      return "";
    }

    if (customer.name === "" && customer.business !== "") {
      return customer.business;
    }

    return customer.name || customer.business || "";
  }

  getInvoiceStatus(invoice: Invoice): string {
    if (invoice?.status === 'refund') return 'refund';
    return invoice?.isReconciled ? 'reconciled' : invoice?.status || '';
  }

  activateTab(tab: string) {
    this.activeTab = tab;
    if (tab === 'paid' || tab === 'reconciled' || tab === 'refunds') {
      this.router.navigate(['/invoices', tab]);
    } else if (tab === 'payments') {
      this.router.navigate(['/invoices', 'payments']);
    } else {
      this.router.navigate(['/invoices']);
    }
  }

  loadMore(tab: 'active' | 'paid' | 'reconciled' | 'refunds' | 'payments') {
    if (tab === 'active') {
      this.dataSourceActive.data = this.allActive;
    } else if (tab === 'refunds') {
      this.dataSourceRefunds.data = this.allRefunds;
    } else if (tab === 'reconciled') {
      this.dataSourceReconciled.data = this.allReconciled;
    } else if (tab === 'payments') {
      this.dataSourcePayments.data = this.allPayments;
    } else {
      this.dataSourceCompleted.data = this.allCompleted;
    }
    this.cdr.detectChanges();
  }

  get activeFilters(): string[] {
    const filters: string[] = [];
    if (this.invoiceType) {
      filters.push(`Type: ${this.invoiceType}`);
    }
    if (this.paymentMethodFilter) {
      filters.push(`Payment: ${this.paymentMethodFilter}`);
    }
    return filters;
  }

  clearFilters() {
    if (!this.invoiceType && !this.paymentMethodFilter) {
      return;
    }
    this.invoiceType = null;
    this.paymentMethodFilter = null;
    this.persistFilters();
    this.loadInvoices();
  }

  private loadFiltersFromStorage() {
    try {
      const raw = localStorage.getItem(this.filterStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      this.invoiceType = parsed?.invoiceType ?? this.invoiceType;
    this.paymentMethodFilter = parsed?.paymentMethodFilter ?? this.paymentMethodFilter;
    } catch (e) {
      console.warn('Failed to load invoice filters from storage', e);
    }
  }

  getLabel() {
    switch (this.dateMode) {
      case DateMode.Weekly:
        return ["Previous Week", "Next Week"];
      case DateMode.Monthly:
        return ["Previous Month", "Next Month"];
      case DateMode.Quaterly:
        return ["Previous Quater", "Next Quater"];
      case DateMode.Yearly:
        return ["Previous Year", "Next Year"];
      case DateMode.AllTime:
        return ["", ""];
      case DateMode.Custom:
        return null;
    }
  }

  nextDateRange() {
    if (this.dateOffset > 0) {
      this.dateOffset--;
      this.findDateRange(this.dateMode, this.dateOffset);
      this.loadInvoices();
    }
  }

  previousDateRange() {
    this.dateOffset++;
    this.findDateRange(this.dateMode, this.dateOffset);
    this.loadInvoices();
  }

  getDateRange() {
    const stored = localStorage.getItem(this.dateStorageKey);
    if (stored == null) {
      this.findDateRange(DateMode.AllTime, 0);
    }

    const temp = JSON.parse(localStorage.getItem(this.dateStorageKey));

    this.dateOffset = temp?.offset == null ? 0 : +temp.offset;
    this.dateMode = temp?.dateMode == null ? DateMode.Weekly : +temp.dateMode;

    const parsedRange = {
      start: new Date(temp.start),
      end: new Date(temp.end)
    };

    this.dateRange = parsedRange;
    return parsedRange;
  }

  setDateRange(startDate: Date, endDate: Date, dateMode: DateMode, offset: number) {
    this.dateMode = dateMode;
    this.dateOffset = offset;
    this.dateRange = { start: startDate, end: endDate };
    localStorage.setItem(this.dateStorageKey, JSON.stringify({
      start: startDate,
      end: endDate,
      dateMode: dateMode,
      offset: offset
    }));
  }

  findDateRange(dateMode: DateMode, dateOffset: number) {
    switch (dateMode) {
      case DateMode.Weekly:
        this.getWeekRange(dateOffset);
        break;
      case DateMode.Monthly:
        this.getMonthlyRange(dateOffset);
        break;
      case DateMode.Quaterly:
        this.getQuaterlyRange(dateOffset);
        break;
      case DateMode.Yearly:
        this.getYearlyRange(dateOffset);
        break;
      case DateMode.AllTime:
        this.getAllTimeRange();
    }
  }

  getMonthlyRange(monthOffset: number) {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() - monthOffset;

    const targetYear = year + Math.floor(month / 12);
    const targetMonth = (month % 12 + 12) % 12;
    const numDays = new Date(targetYear, targetMonth + 1, 0).getDate();
    const startDate = new Date(targetYear, targetMonth, 1);
    const endDate = new Date(targetYear, targetMonth, numDays, 23, 59, 59, 999);

    this.setDateRange(startDate, endDate, DateMode.Monthly, monthOffset);
  }

  getYearlyRange(yearOffset: number) {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const targetYear = currentYear - yearOffset;

    if (currentDate.getMonth() > 6) {
      this.setDateRange(
        new Date(targetYear, 6, 1, 0, 0, 0, 0),
        new Date(targetYear + 1, 5, 30, 23, 59, 59, 999),
        DateMode.Yearly,
        yearOffset
      );
    } else {
      this.setDateRange(
        new Date(targetYear - 1, 6, 1, 0, 0, 0, 0),
        new Date(targetYear, 5, 30, 23, 59, 59, 999),
        DateMode.Yearly,
        yearOffset
      );
    }
  }

  getQuaterlyRange(dateOffset = 0) {
    const now = new Date();
    let year = now.getFullYear();
    let month = now.getMonth();

    month -= dateOffset * 3;
    while (month < 0) {
      month += 12;
      year--;
    }

    let quarterStartMonth;

    if (month >= 6 && month <= 8) {
      quarterStartMonth = 6;
    } else if (month >= 9 && month <= 11) {
      quarterStartMonth = 9;
    } else if (month >= 0 && month <= 2) {
      quarterStartMonth = 0;
    } else {
      quarterStartMonth = 3;
    }

    const startDate = new Date(year, quarterStartMonth, 1);
    let endDate;

    if (quarterStartMonth === 6 || quarterStartMonth === 9) {
      endDate = new Date(year, quarterStartMonth + 2, quarterStartMonth === 6 ? 30 : 31, 23, 59, 59, 999);
    } else if (quarterStartMonth === 0) {
      endDate = new Date(year, 2, 31, 23, 59, 59, 999);
    } else {
      endDate = new Date(year, 5, 30, 23, 59, 59, 999);
    }

    this.setDateRange(
      startDate,
      endDate,
      DateMode.Quaterly,
      dateOffset);
  }

  getWeekRange(weekOffset: number) {
    const todayUtc = new Date();
    const base = new Date(Date.UTC(todayUtc.getUTCFullYear(), todayUtc.getUTCMonth(), todayUtc.getUTCDate()));
    const day = base.getUTCDay() || 7; // 1 = Monday ... 7 = Sunday
    // Move to Monday of the target week
    base.setUTCDate(base.getUTCDate() - (day - 1) - (weekOffset * 7));

    const start = new Date(base);
    start.setUTCHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setUTCDate(start.getUTCDate() + 6);
    end.setUTCHours(23, 59, 59, 999);

    this.setDateRange(start, end, DateMode.Weekly, weekOffset);
  }

  getAllTimeRange() {
    this.setDateRange(
      new Date("1970-01-01"),
      new Date(),
      DateMode.AllTime,
      0
    );
  }

  switchToWeekly() {
    this.dateOffset = 0;
    this.dateMode = DateMode.Weekly;
    this.findDateRange(DateMode.Weekly, this.dateOffset);
    this.loadInvoices();
  }

  switchToMonthly() {
    this.dateOffset = 0;
    this.dateMode = DateMode.Monthly;
    this.findDateRange(DateMode.Monthly, this.dateOffset);
    this.loadInvoices();
  }

  switchToQuaterly() {
    this.dateOffset = 0;
    this.dateMode = DateMode.Quaterly;
    this.findDateRange(DateMode.Quaterly, this.dateOffset);
    this.loadInvoices();
  }

  switchToYearly() {
    this.dateOffset = 0;
    this.dateMode = DateMode.Yearly;
    this.findDateRange(DateMode.Yearly, this.dateOffset);
    this.loadInvoices();
  }

  switchToAllTime() {
    this.dateOffset = 0;
    this.dateMode = DateMode.AllTime;
    this.findDateRange(DateMode.AllTime, this.dateOffset);
    this.loadInvoices();
  }

  private buildDateExpression() {
    if (!this.dateRange || this.dateMode === DateMode.AllTime) {
      return null;
    }
    const start = this.dateRange.start;
    const end = this.dateRange.end;
    return {
      $and: [
        { $gte: [ { $toDate: "$dateCreated" }, start ] },
        { $lte: [ { $toDate: "$dateCreated" }, end ] }
      ]
    };
  }

  private sortInvoices(invoices: Invoice[]): Invoice[] {
    return this.filterByDate(invoices || [], (inv) => inv?.dateCreated).sort((a, b) => {
      const ad = new Date(a?.dateCreated || 0).getTime();
      const bd = new Date(b?.dateCreated || 0).getTime();
      return bd - ad;
    });
  }

  private sortPayments(rows: InvoicePaymentRow[]): InvoicePaymentRow[] {
    return this.filterByDate(rows || [], (row) => row?.payment?.date || row?.dateCreated).sort((a, b) => {
      const ad = new Date(a?.payment?.date || a?.dateCreated || 0).getTime();
      const bd = new Date(b?.payment?.date || b?.dateCreated || 0).getTime();
      return bd - ad;
    });
  }

  private filterByDate<T>(items: T[], getDate: (item: T) => any): T[] {
    if (!items?.length || !this.dateRange || this.dateMode === DateMode.AllTime) {
      return items;
    }
    const start = this.dateRange.start.getTime();
    const end = this.dateRange.end.getTime();
    return items.filter(item => {
      const d = new Date(getDate(item) || 0).getTime();
      return d >= start && d <= end;
    });
  }

  private persistFilters() {
    try {
      const payload = {
        invoiceType: this.invoiceType,
        paymentMethodFilter: this.paymentMethodFilter
      };
      localStorage.setItem(this.filterStorageKey, JSON.stringify(payload));
    } catch (e) {
      console.warn('Failed to persist invoice filters', e);
    }
  }
}

// setToMonday no longer used; kept for reference if needed elsewhere in this file
